import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartDate,
  CartDetailsModal,
  CartNotes,
  // CartPublic,
  CartTags,
  ConfirmActionModal,
} from 'src/components';
import { useDisassociateCart } from 'src/hooks';
import EditIcon from 'src/images/icons/edit-icon.svg?react';
import TrashIcon from 'src/images/icons/trash-icon.svg?react';
import { getCartIdAtom } from 'src/store';
import type { CartGetItem } from 'src/types';

type Props = {
  cartTimestamp: number;
  cartListed: boolean;
  cartNotes: string;
  cartTags: string[];
  cartTitle: string | undefined;
  cartCreated: boolean;
  cartStore: string | undefined;
  cartCCY: string;
  cartCCYS: string;
  cartItems: CartGetItem[];
  displayTrashIcon?: boolean;
};

export const GetReviewOwnership = ({
  cartTimestamp,
  cartListed,
  cartNotes,
  cartTags,
  cartTitle,
  cartCreated,
  cartStore,
  cartCCY,
  cartCCYS,
  cartItems,
  displayTrashIcon = true,
}: Props) => {
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showConfirmActionModal, setShowConfirmActionModal] = useState<boolean>(false);
  const [cartId] = useAtom(getCartIdAtom);

  const { t } = useTranslation();
  const disassociateCart = useDisassociateCart();

  const handleDelete = () => {
    disassociateCart.mutate({ cartId });
    setShowConfirmActionModal(false);
  };

  return (
    <div className='min-h-[52px] w-full bg-cloudGray py-2'>
      <div className='mx-auto flex w-full max-w-[1440px] flex-row flex-wrap items-start justify-between gap-x-2 gap-y-2 px-2 xs:gap-x-4 xs:px-4 sm:flex-nowrap md:px-10 lg:px-12'>
        <div className='order-1 flex min-h-[36px] min-w-min grow-0 flex-row items-center justify-start gap-4'>
          <CartDate cartTimestamp={cartTimestamp} forGetPage={true} />
          {/* <CartPublic cartId={cartId} cartListed={cartListed} forGetPage={true} /> */}
          <CartNotes cartId={cartId} cartNotes={cartNotes} forGetPage={true} />
        </div>
        <div className='grow-1 order-3 mr-auto flex w-full flex-row items-center justify-start sm:order-2'>
          <CartTags cartId={cartId} cartTags={cartTags} forGetPage={true} />
        </div>
        <div className='order-2 flex grow-0 flex-row items-center justify-center gap-2 xs:gap-4 sm:order-3'>
          <button
            onClick={() => setShowDetailsModal(true)}
            className='group button-secondary h-9 gap-1 px-4 text-sm leading-[14px] xs:gap-2 xs:px-6'
          >
            <EditIcon className='h-5 w-5 text-primary' />
            {t('get.details')}
          </button>
          {displayTrashIcon && (
            <TrashIcon
              onClick={() => setShowConfirmActionModal(true)}
              className='h-auto w-5 cursor-pointer stroke-darkSteel transition-all hover:stroke-gray-400'
            />
          )}
        </div>
        <CartDetailsModal
          cartId={cartId}
          cartTitle={cartTitle || ''}
          cartListed={cartListed}
          cartNotes={cartNotes}
          cartTags={cartTags}
          cartCreated={cartCreated}
          show={showDetailsModal}
          setShow={setShowDetailsModal}
          cartCCY={cartCCY}
          cartCCYS={cartCCYS}
          cartItems={cartItems}
          cartTimestamp={cartTimestamp}
          cartStore={cartStore}
        />

        <ConfirmActionModal
          show={showConfirmActionModal}
          setShow={setShowConfirmActionModal}
          onClickPrimary={handleDelete}
          titleText={t('get.are_you_sure')}
          bodyText={t('get.deleting_cart_will_remove_it_from_your_cart_history')}
        />
      </div>
    </div>
  );
};
